import { useState, type ComponentProps } from "react"
import { formatNumber } from "@/v2-common/utils/utils.formatting"
import cn from "@/v2-ui/utils/utils.cn"
import Link from "@/v2-backoffice-client/components/link"
import PortfolioSectionHeader from "@/v2-backoffice-client/portfolio/PortfolioSectionHeader"
import UsageReportCard from "@/v2-backoffice-client/usage/report/UsageReportCard"
import PortfolioCustomerListDialog from "@/v2-backoffice-client/portfolio/PortfolioCustomerListDialog"
import UsageTrendBadge from "@/v2-backoffice-client/usage/UsageTrendBadge"
import { MOVER_LIMIT } from "@/v2-backoffice-client/portfolio/portfolio.constants"
import type {
  PortfolioMover,
  PortfolioMovers as PortfolioMoversLists,
} from "@/v2-backoffice-client/portfolio/portfolio.health.utils"
import { buildCustomerActivityLink } from "@/v2-backoffice-client/portfolio/portfolio.utils"
import type { ColumnMetaMap } from "@/v2-backoffice-client/components/table"
import {
  createCustomerColumns,
  customerColumns,
} from "@/v2-backoffice-client/portfolio/portfolio.customerTable.columns"

export type PortfolioMoversProps = ComponentProps<"section"> & {
  /** FULL sorted lists — the card previews the top few per direction. */
  movers: PortfolioMoversLists
}

/**
 * "2 133 → 4 308" means nothing on its own — every pair is labeled with
 * what it measures and which windows it compares.
 */
const PAIR_MEANING =
  "Weighted usage actions: previous 28 days → last 28 days. Weighted = heavy actions (exports, reports, saves) count more than clicks."

const CUSTOMER = customerColumns<PortfolioMover>()

/** The movers dialog: the account, its churn read, the pair and the change. */
const MOVER_COLUMN_METAS: ColumnMetaMap<PortfolioMover> = {
  name: CUSTOMER.name,
  churn: CUSTOMER.churn,
  previous: {
    id: "previous",
    label: "Previous 28 d",
    description: PAIR_MEANING,
    getValue: (row) => row.previous,
    defaultSize: 120,
    className: "tabular-nums",
    renderCell: ({ row }) => formatNumber(Math.round(row.original.previous)),
  },
  current: {
    id: "current",
    label: "Last 28 d",
    description: PAIR_MEANING,
    getValue: (row) => row.current,
    defaultSize: 120,
    className: "tabular-nums",
    renderCell: ({ row }) => formatNumber(Math.round(row.original.current)),
  },
  delta: {
    id: "delta",
    label: "Change",
    description:
      "Last 28 days against the previous 28; the badge is the same pair in percent.",
    getValue: (row) => row.delta,
    defaultSize: 110,
    renderCell: ({ row }) => <MoverPair mover={row.original} />,
  },
  billed: CUSTOMER.billed,
}
const MOVER_COLUMNS = createCustomerColumns<PortfolioMover>(MOVER_COLUMN_METAS)

// The week's call lists: the customers whose 28-day activity swung the
// most, biggest swing first — "12,000 → 600" names the account that
// matters, where a percentage list just ties everyone at -100%. Names
// deep-link into the customer's Activity tab where the swing is explained.
function PortfolioMovers(props: PortfolioMoversProps) {
  const { movers, className, ...sectionProps } = props

  const [openDirection, setOpenDirection] = useState<
    "falling" | "rising" | null
  >(null)

  const dialogMovers = openDirection === null ? null : movers[openDirection]

  return (
    <section className={cn("flex flex-col gap-4", className)} {...sectionProps}>
      <PortfolioSectionHeader
        eyebrow="Movers"
        title="Top movers"
        lead="The accounts whose activity moved most against the four weeks before — falling ones to check in on, rising ones to time an expansion."
        info="Weighted 28-day activity vs the 28 days before, whole account, ranked by the size of the swing. Very small accounts are excluded so the lists mean something."
      >
        <span className="ml-auto text-xs text-muted-foreground">
          previous four weeks → last four weeks
        </span>
      </PortfolioSectionHeader>

      <div className="usage-report-grid flex-1">
        <UsageReportCard title="Falling — check in">
          <MoversList
            movers={movers.falling}
            emptyText="No customers falling past the noise floor."
            onShowAll={() => setOpenDirection("falling")}
          />
        </UsageReportCard>
        <UsageReportCard title="Rising — expansion timing">
          <MoversList
            movers={movers.rising}
            emptyText="No customers rising past the noise floor."
            onShowAll={() => setOpenDirection("rising")}
          />
        </UsageReportCard>
      </div>

      {dialogMovers != null && (
        <PortfolioCustomerListDialog
          open
          onOpenChange={(next) => {
            if (!next) setOpenDirection(null)
          }}
          title={
            openDirection === "falling"
              ? `All falling customers — ${formatNumber(dialogMovers.length)}`
              : `All rising customers — ${formatNumber(dialogMovers.length)}`
          }
          description={`Biggest swing first. ${PAIR_MEANING}`}
          tableId="portfolio-movers-dialog"
          rows={dialogMovers}
          columns={MOVER_COLUMNS}
          initialSorting={[{ id: "delta", desc: openDirection === "rising" }]}
        />
      )}
    </section>
  )
}

type MoversListProps = {
  movers: PortfolioMover[]
  emptyText: string
  onShowAll(): void
}

function MoversList(props: MoversListProps) {
  const { movers, emptyText, onShowAll } = props
  const visible = movers.slice(0, MOVER_LIMIT)
  const hiddenCount = movers.length - visible.length

  return (
    <div className="flex min-w-0 flex-col gap-1.5">
      {movers.length === 0 ? (
        <p className="text-xs text-muted-foreground">{emptyText}</p>
      ) : (
        <>
          <div className="usage-report-list-head gap-2">
            <span className="min-w-0 flex-1">Customer</span>
            <span className="text-right">Previous → last 28 d</span>
            <span className="w-16 text-center">Change</span>
          </div>
          <ul className="flex flex-col gap-2.5">
            {visible.map((mover) => (
              <li
                key={mover.customer.externalCustomerId}
                className="flex items-center gap-2"
              >
                <Link
                  to={buildCustomerActivityLink(
                    mover.customer.externalCustomerId,
                  )}
                  iconSrc={null}
                  className="min-w-0 flex-1 truncate font-medium"
                >
                  {mover.customer.name}
                </Link>
                <MoverPair mover={mover} showPair />
              </li>
            ))}
          </ul>
          {hiddenCount > 0 && (
            <button
              type="button"
              className="self-start text-xs text-muted-foreground underline-offset-2 hover:underline"
              onClick={onShowAll}
            >
              Show all {formatNumber(movers.length)} (+
              {formatNumber(hiddenCount)})
            </button>
          )}
        </>
      )}
    </div>
  )
}

/**
 * The labeled swing: previous-window volume → current-window volume plus
 * the change badge computed from the SAME pair (a badge from a different
 * measure would contradict the numbers and read as a bug).
 */
function MoverPair(props: { mover: PortfolioMover; showPair?: boolean }) {
  const { mover, showPair = false } = props
  return (
    <>
      {showPair && (
        <span
          className="shrink-0 text-xs tabular-nums text-muted-foreground"
          title={PAIR_MEANING}
        >
          {formatNumber(Math.round(mover.previous))}
          {" → "}
          {formatNumber(Math.round(mover.current))}
        </span>
      )}
      <UsageTrendBadge
        trendPct={
          mover.previous > 0
            ? ((mover.current - mover.previous) / mover.previous) * 100
            : null
        }
        className="w-16 shrink-0 justify-center"
      />
    </>
  )
}

export default PortfolioMovers
